import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Search, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { NotificationPrefsProvider } from "@/contexts/NotificationPrefs";
import { PomodoroProvider } from "@/contexts/PomodoroContext";
import { WellbeingEngineProvider } from "@/contexts/WellbeingEngine";
import { PomodoroButton } from "@/components/PomodoroButton";
import { WorkingDayTimeline } from "@/components/WorkingDayTimeline";
import { AppetisedIcon } from "@/components/BrandMarks";
import { PinCurrentViewButton } from "@/components/PinCurrentViewButton";
import { PinnedTabsBar } from "@/components/PinnedTabsBar";

export function Layout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const [search, setSearch] = useState("");

  useEffect(() => {
    const query = new URLSearchParams(window.location.search).get("q") ?? "";
    setSearch(query);
  }, [location]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = search.trim();
    setLocation(query ? `/search?q=${encodeURIComponent(query)}` : "/");
  };

  return (
    <NotificationPrefsProvider>
    <PomodoroProvider>
    <WellbeingEngineProvider>
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-background font-sans text-foreground selection:bg-primary/30">
      <header className="relative z-20 flex h-16 shrink-0 items-stretch overflow-hidden bg-card/20">
        {/* appetised rebrand: the middle stop moved off the old cyan accent
            onto brand gold, with navy flanking on light and periwinkle
            flanking on dark — an asymmetric 3-color gradient the shared
            --primary/--accent tokens alone can't express (dark's flanking
            color isn't light theme's --primary), so this is the literal
            hex stops from the design handoff rather than token references. */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,transparent_0%,transparent_15%,#1B3FAA4D_30%,#C9A63C6B_50%,#6F8FE04D_70%,transparent_85%,transparent_100%)] dark:bg-[linear-gradient(115deg,transparent_0%,transparent_15%,#6F8FE06B_30%,#E3C76680_50%,#6F8FE06B_70%,transparent_85%,transparent_100%)] dark:opacity-60" />
        {/* Fixed w-16 to match the icon rail below (FolderNav.tsx) exactly —
            this column's own right border lines up with the rail's, so the
            divider reads as one continuous line from the very top of the
            page instead of stopping at the header's bottom edge. No bottom
            border or shadow on this column itself, on purpose — the icon
            rail directly below picks up right where this leaves off with no
            horizontal line crossing between them, only the vertical one
            continuing down. (shadow-sm moved here from the header itself —
            box-shadow doesn't respect this column/wrapper split the way a
            border does, it was still rendering under the logo regardless.) */}
        <Link
          href="/"
          aria-label="Go to inbox"
          className="relative z-10 flex w-16 shrink-0 items-center justify-center border-r border-border"
        >
          <AppetisedIcon className="h-9 w-9" />
        </Link>

        {/* The bottom border + shadow live on this wrapper (everything but
            the logo column), not the header itself — see the comment above. */}
        <div className="relative z-10 flex min-w-0 flex-1 items-stretch border-b border-border shadow-sm">
          {/* Back/forward and Pomodoro flank the search bar directly, centered
              together as one cluster — rather than back/forward pinned by the
              logo and Pomodoro pinned by AI Chat, with the search bar
              independently centered between them and no visual relationship
              to either. */}
          <div className="flex flex-1 items-center justify-center gap-2 px-5">
            <div className="flex shrink-0 items-center gap-0.5 rounded-md border-2 border-border p-0.5">
              <button
                type="button"
                onClick={() => window.history.back()}
                aria-label="Back"
                title="Back"
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="h-4 w-px shrink-0 bg-border" />
              <button
                type="button"
                onClick={() => window.history.forward()}
                aria-label="Forward"
                title="Forward"
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={submitSearch} className="w-full max-w-lg">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="input-global-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search emails, contacts, channels…"
                  aria-label="Search emails, contacts, channels"
                  className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                />
              </div>
            </form>

            <WorkingDayTimeline />
            <PomodoroButton />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-2 px-5">
            {/* Lives in the header rather than the inbox toolbar so pinning
                works on every screen — the inbox, settings, a doc, the
                calendar — not just the ones that happen to have a toolbar. */}
            <PinCurrentViewButton />
            <button
              type="button"
              onClick={() => setLocation("/ai-chat")}
              aria-label="Open AI Chat"
              title="AI Chat"
              className="flex shrink-0 items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
            >
              <Sparkles className="h-4 w-4 text-primary" />
              AI Chat
            </button>
          </div>
        </div>
      </header>
      <main className="flex min-h-0 flex-1 flex-col bg-background">
        {children}
      </main>
      {/* Renders nothing at all until the first pin exists, so a user who has
          never pinned anything gets exactly the layout they had before this
          feature — no empty bar, no reserved strip. Full width, from the left
          edge of the nav rail to the far right. */}
      <PinnedTabsBar />
    </div>
    </WellbeingEngineProvider>
    </PomodoroProvider>
    </NotificationPrefsProvider>
  );
}
